import React, { memo, useCallback, useEffect, useRef, useState } from "react";


import "./styles.css";
import TextField from "../textfield/textfield";

function TypeaheadSelects({
    disabled,
    icon,
    id,
    isLoading,
    name,
    options,
    placeholder,
    values,
    onBlur,
    onClear,
    onSelect,
    invalid,
    minLengthSearch = 0,
}) {
    const [_values, _setValues] = useState(values);
    const [_textValue, _setTextValue] = useState("");
    const [_hasFocus, _setHasFocus] = useState(false);
    const [_hasOptionsOpen, _setHasOptionsOpen] = useState(false);
    const [_currentOptions, _setCurrentOptions] = useState(options[_values.length]);
    const _valuesLength = _values.length;
    const valuesText = _values
        .map(({ label }, index) =>
            index + 1 < options.length ? `${label.trim()} | ` : label.trim()
        )
        .join("");
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const setCurrentOptionsCallback = useCallback(
        _options => {
            _setCurrentOptions(_options[_valuesLength]);
        },
        [_valuesLength, _setCurrentOptions]
    );

    useEffect(() => {
        _setValues(values);
    }, [values]);
    useEffect(() => {
        const listener = event => {
            if (containerRef.current && containerRef.current.contains(event.target))
                return;
            _setHasOptionsOpen(false);
            _setHasFocus(false);
        };

        if (_hasFocus) {
            document.addEventListener("mousedown", listener);
            document.addEventListener("touchstart", listener);
        }
        return () => {
            document.removeEventListener("mousedown", listener);
            document.removeEventListener("touchstart", listener);
        };
    }, [containerRef, _hasFocus, _setHasOptionsOpen]);
    useEffect(() => {
        setCurrentOptionsCallback(options);
        if (options.length > 0 && _hasFocus) {
            if (
                !!_textValue &&
                (minLengthSearch <= 0 || _textValue.length >= minLengthSearch)
            ) {
                _setHasOptionsOpen(true);
            }
        }
        if (inputRef && inputRef.current && _hasFocus) inputRef.current.focus();
    }, [_hasFocus, options, setCurrentOptionsCallback]);
    useEffect(() => {
        if (values.length && _values.length) _setValues(values);
    }, [values, _values, _setValues]);

    function handleFocus() {
        _setHasFocus(true);
        if ((valuesText || _textValue) && !_hasOptionsOpen) {
            if (
                !!_textValue &&
                (minLengthSearch <= 0 || _textValue.length >= minLengthSearch)
            ) {
                _setHasOptionsOpen(true);
            }
        }
    }
    function handleSelect({ label, value }) {
        const valuesCopy = _values.slice();

        valuesCopy.push({ label, value });
        onSelect({ label, value });
        _setValues(valuesCopy);
        setTimeout(() => {
            _setTextValue("");
        }, 0);
    }
    function handleTextValue(value) {
        const mustHaveOptionsOpen =
            !!value && (minLengthSearch <= 0 || value.length >= minLengthSearch);
        if (mustHaveOptionsOpen !== _hasOptionsOpen) {
            _setHasOptionsOpen(!_hasOptionsOpen);
        }
        _setTextValue(value);
    }
    function handleClear() {
        _setTextValue("");
        _setValues([]);
        _setHasOptionsOpen(false);
        onClear();
        if (inputRef.current) inputRef.current.focus();
    }
    function _handleScrollList(event) {
        const { activeElement } = document;
        const { nextSibling, previousSibling } = activeElement;
        const { firstChild } = document.getElementById(`typeahead-selects-list-${id}`);

        event.preventDefault();
        switch (event.keyCode) {
            case 38: // if the UP key is pressed
                if (activeElement === firstChild) {
                    inputRef.current.focus();
                } else if (previousSibling && previousSibling.focus)
                    previousSibling.focus();
                break;
            case 40: // if the DOWN key is pressed
                if (activeElement === inputRef.current && firstChild.focus)
                    firstChild.focus();
                else if (nextSibling && nextSibling.focus) nextSibling.focus();
                break;
            default:
                break;
        }
    }
    function _handleKeyDown(event) {
        const { keyCode } = event;
        const backspaceKeyCode = 8;
        const leftArrowKeyCode = 37;
        const homeKeyCode = 36;
        const tabKey = keyCode === 9;

        if (
            keyCode === 13 ||
            (backspaceKeyCode === event.keyCode && _textValue === "") ||
            (_valuesLength && [homeKeyCode, leftArrowKeyCode].includes(keyCode))
        )
            event.preventDefault();
        else if ((event.shiftKey && tabKey) || tabKey) _setHasOptionsOpen(false);
        else if ((keyCode === 38 || keyCode === 40) && _hasOptionsOpen)
            _handleScrollList(event);
    }
    return (
        <div className="type-ahead-selects" ref={containerRef}>
            <div>
                <TextField
                    autoComplete="off"
                    disabled={disabled}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    readOnly={_valuesLength === options.length}
                    ref={inputRef}
                    value={`${valuesText}${_textValue}`}
                    onBlur={onBlur}
                    onChange={({ target }) =>
                        handleTextValue(target.value.replace(valuesText, ""))
                    }
                    fluid
                    clearable
                    loading={isLoading}
                    onClear={handleClear}
                    onFocus={handleFocus}
                    onKeyDown={_handleKeyDown}
                    icon={icon}
                    invalid={invalid}
                />
            </div>
            {_hasOptionsOpen && _currentOptions && (
                <ul id={`typeahead-selects-list-${id}`} tabIndex="0">
                    {_currentOptions
                        .filter(({ label }) =>
                            label.toLowerCase().includes(_textValue.toLowerCase())
                        )
                        .map(({ label, value }) => (
                            <li
                                key={`typeahead-selects-${id}-${value}`}
                                role="presentation"
                                tabIndex="-1"
                                onClick={() => handleSelect({ label, value })}
                                onKeyDown={event => {
                                    if (event.keyCode === 13) {
                                        event.preventDefault();
                                        handleSelect({ label, value });
                                    } else _handleKeyDown(event);
                                }}
                            >
                                {label}
                            </li>
                        ))}
                </ul>
            )}
        </div>
    );
}

export default memo(TypeaheadSelects);
